// src/components/AddressAutocomplete.js
import React, { useEffect, useRef, useState } from "react";

export default function AddressAutocomplete({
  value,
  onSelect,
  onManualInput,
  placeholder,
  className,
  country = "USA",
}) {
  const ref = useRef();
  const [isReady, setIsReady] = useState(false);

  useEffect(() => {
    const interval = setInterval(() => {
      // New versions expose the element as google.maps.importLibrary
      if (window.google && customElements.get("gmpx-place-autocomplete")) {
        setIsReady(true);
        clearInterval(interval);
      }
    }, 300);
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    if (!isReady || !ref.current) return;
    const el = ref.current;

    const countryCode = country === "CANADA" ? "CA" : "US";
    el.componentRestrictions = { country: [countryCode] };

    const handler = (e) => {
      const place = e.detail?.place;
      if (!place) return;

      const comps = place.address_components || [];
      const get = (type, field = "short_name") =>
        comps.find((c) => c.types.includes(type))?.[field] || "";

      const streetNumber = get("street_number");
      const route = get("route", "long_name");
      const line1 = [streetNumber, route].filter(Boolean).join(" ");
      const city =
        get("locality") || get("sublocality") || get("postal_town") || "";
      const state = get("administrative_area_level_1");
      const zip = get("postal_code");
      const countryCodeShort = get("country", "short_name");

      onSelect({
        line1,
        city,
        state,
        zip,
        country: countryCodeShort === "CA" ? "CANADA" : "USA",
        lat: place.geometry?.location?.lat() ?? null,
        lng: place.geometry?.location?.lng() ?? null,
      });
    };

    el.addEventListener("gmpx-placechange", handler);

    // Detect typing manually
    const inputField = el.querySelector("input");
    if (inputField) {
      inputField.addEventListener("input", (e) => {
        onManualInput?.(e.target.value);
      });
    }

    return () => {
      el.removeEventListener("gmpx-placechange", handler);
      if (inputField) inputField.removeEventListener("input", onManualInput);
    };
  }, [isReady, onSelect, onManualInput, country]);

  if (!isReady) {
    return (
      <input
        placeholder={placeholder || "Enter address"}
        value={value || ""}
        onChange={(e) => onManualInput?.(e.target.value)}
        className={className}
      />
    );
  }

  return (
    <gmpx-place-autocomplete
      ref={ref}
      placeholder={placeholder || "Enter address"}
      value={value || ""}
      class={className}
    />
  );
}